import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到"
          description="抱歉，您查找的文章不存在或已被移除。"
          keywords="爱情岛一论坛"
        />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1 pt-16 flex items-center justify-center">
            <div className="text-center px-4">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold font-heading text-foreground-900 mb-3">文章未找到</h1>
              <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.categoryId, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    author: {
      '@type': 'Organization',
      name: '爱情岛路线导航',
    },
    publisher: {
      '@type': 'Organization',
      name: '爱情岛路线导航',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://aiqingdao-line.com${item.href}` : `https://aiqingdao-line.com/article/${article.slug}`,
    })),
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://aiqingdao-line.com/article/${article.slug}`}
        ogType="article"
        schemaJson={[articleSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <article className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="text-xs text-foreground-500">{article.publishedAt} 发布</span>
                  <span className="text-xs text-foreground-500">·</span>
                  <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
                  {article.updatedAt !== article.publishedAt && (
                    <>
                      <span className="text-xs text-foreground-500">·</span>
                      <span className="text-xs text-foreground-500">更新于 {article.updatedAt}</span>
                    </>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 text-xs border border-background-200/70">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground-600">分享文章：</span>
                    {[
                      { icon: 'ri-wechat-line', label: '微信' },
                      { icon: 'ri-weibo-line', label: '微博' },
                      { icon: 'ri-link', label: '复制链接' },
                    ].map((item) => (
                      <button
                        key={item.label}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-all cursor-pointer"
                        title={item.label}
                      >
                        <i className={`${item.icon} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    返回{article.categoryName}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-12 pt-8 border-t border-background-200/70">
                  <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-6">
                    相关文章推荐
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.slug}`}
                        className="group p-5 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                      >
                        <h4 className="text-sm font-semibold font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{related.excerpt}</p>
                        <span className="text-xs text-primary-600 font-medium flex items-center gap-1">
                          阅读全文
                          <i className="ri-arrow-right-s-line text-sm"></i>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}